import { marginPctFromFactor } from '@timelog/pm-shared';
import type { BudgetModelConfig, TargetReading } from '@timelog/pm-shared';
import type { TFunction } from 'i18next';

import { i18n } from '@/i18n';
import { formatFactor } from '@/lib/format';

/**
 * How a factor-shaped number is SHOWN under the configured display metric
 * (ADR 0027): the budget model stores every ratio as a factor and converts on
 * display — margin% = 1 − 1/factor (CALC-10). Every surface that names the
 * metric reads these four helpers, so a tile cannot say "factor" while the
 * configuration runs margin (the aside's project totals did exactly that).
 *
 * Pure so the copy is assertable — `metricReading.unit.ts`. The copy comes
 * from the translation `t` passed in (ADR 0049).
 */

export type BudgetMetric = BudgetModelConfig['metric'];

/**
 * The arrow beside a factor, read against a reference factor (the contract
 * aside's budgeted factor against its target since 2026-09-29; the task
 * panel's forecasted factor against the contract's own factor since
 * 2026-10-29): up in the success tone at or above the reference, flat in the
 * warning tone from ×1.5 up to it, down in coral below ×1.5. No figure or no
 * reference → flat, muted — nothing to compare.
 */
export type FactorTrend = {
  icon: 'trending_up' | 'trending_flat' | 'trending_down';
  color: string;
};

export const factorTrend = (factor: number | null, reference: number | null): FactorTrend =>
  factor === null || reference === null
    ? { icon: 'trending_flat', color: 'var(--fg-3)' }
    : factor >= reference
      ? { icon: 'trending_up', color: 'var(--color-success-80)' }
      : factor >= 1.5
        ? { icon: 'trending_flat', color: 'var(--color-warning-80)' }
        : { icon: 'trending_down', color: 'var(--color-coral-80)' };

/** The `t` the Financials copy helpers take: both namespaces they read. */
export type FinancialsT = TFunction<readonly ['pmFinancials', 'pmCommon']>;

/** The figures every rollup carries (CALC-16 project, per-contract summary). */
export type MetricFigures = {
  budgetedFactor: number | null;
  /** Already rounded to a whole percent by the calculation layer. */
  marginPct: number | null;
};

/**
 * "Factor" | "Margin" — title case, for tile captions. `t` defaults to the
 * app's instance so callers outside Financials keep working unchanged.
 */
export const metricLabel = (
  metric: BudgetMetric,
  t: FinancialsT = i18n.getFixedT(null, ['pmFinancials', 'pmCommon']),
): string => (metric === 'factor' ? t('pmCommon:metric.factor') : t('pmCommon:metric.margin'));

/** `×2.13` under factor, `53%` under margin, `—` when there is no cost to divide by. */
export const formatMetricValue = (metric: BudgetMetric, figures: MetricFigures): string => {
  if (metric === 'factor') return formatFactor(figures.budgetedFactor);
  return figures.marginPct === null ? '—' : `${figures.marginPct}%`;
};

/**
 * A factor alone rendered in the configured metric — for figures the
 * calculation layer only carries as a factor (the v1 forecast, CALC-16).
 */
export const formatFactorAs = (metric: BudgetMetric, factor: number | null): string =>
  formatMetricValue(metric, {
    budgetedFactor: factor,
    marginPct: factor === null ? null : Math.round(marginPctFromFactor(factor)),
  });

/** The configured target as a trailing caption: `target ×2.1` / `target margin 52%`. */
export const formatMetricTarget = (
  metric: BudgetMetric,
  target: TargetReading,
  t: FinancialsT,
): string =>
  metric === 'factor'
    ? t('pmFinancials:metric.TargetFactor', { value: formatFactor(target.factor, 1) })
    : t('pmFinancials:metric.TargetMargin', { value: Math.round(target.marginPct) });

/**
 * A rate-card row's uplift in the configured metric: sell rate over cost rate
 * (CALC-10 for the margin rendering). `—` when either side is unknown or
 * zero — a role with no cost price has nothing to compare against, and a
 * 0 rate (TimeLog Internal) has no meaningful margin.
 */
export const formatRateOverCost = (
  metric: BudgetMetric,
  rate: number,
  cost: number | undefined,
): string =>
  cost === undefined || cost <= 0 || rate <= 0 ? '—' : formatFactorAs(metric, rate / cost);

/** The one-line explanation of how the budgeted figure is derived (CALC-10/16). */
export const metricFormula = (metric: BudgetMetric, t: FinancialsT): string =>
  metric === 'factor'
    ? t('pmFinancials:metric.FormulaFactor')
    : t('pmFinancials:metric.FormulaMargin');
